"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import AppLogo from "@/components/ui/AppLogo";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "Services", href: "#services" },
    { label: "About Us", href: "#team" },
    { label: "Process", href: "#process" },
    { label: "Track Record", href: "#results" },
  ];

  return (
    <header
      role="banner"
      className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ${
        scrolled
          ? "bg-[#0C0C0E]/95 backdrop-blur-md border-b border-[rgba(242,239,233,0.06)] py-4"
          : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-8xl mx-auto px-6 lg:px-12 flex items-center justify-between">
        {/* Logo */}
        <Link href="/homepage" className="flex items-center gap-3 group" aria-label="JAMO LLC — Home">
          <AppLogo
            src="/assets/images/white_logo_transparent_background-1771727844744.png"
            size={96}
            className="transition-opacity duration-300 group-hover:opacity-80"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-10" aria-label="Main navigation">
          {navLinks?.map((link) => (
            <a
              key={link?.label}
              href={link?.href}
              className="nav-link text-[11px] font-semibold tracking-[0.2em] uppercase text-[#A09D97] hover:text-[#F2EFE9] transition-colors duration-300"
            >
              {link?.label}
            </a>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-4">
          <a
            href="#contact"
            className="btn-primary text-[11px] font-bold tracking-[0.12em]"
          >
            <span>Schedule a Call</span>
          </a>
        </div>

        {/* Mobile Toggle */}
        <button
          className="md:hidden flex flex-col gap-[5px] p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          aria-controls="mobile-menu"
        >
          <span
            className={`block w-6 h-[1.5px] bg-[#F2EFE9] transition-all duration-300 ${
              mobileOpen ? "rotate-45 translate-y-[6.5px]" : ""
            }`}
          />
          <span
            className={`block w-6 h-[1.5px] bg-[#F2EFE9] transition-all duration-300 ${
              mobileOpen ? "opacity-0" : ""
            }`}
          />
          <span
            className={`block w-6 h-[1.5px] bg-[#F2EFE9] transition-all duration-300 ${
              mobileOpen ? "-rotate-45 -translate-y-[6.5px]" : ""
            }`}
          />
        </button>
      </div>
      {/* Mobile Menu */}
      <div
        id="mobile-menu"
        role="navigation"
        aria-label="Mobile navigation"
        className={`md:hidden transition-all duration-500 overflow-hidden ${
          mobileOpen ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="bg-[#111114] border-t border-[rgba(242,239,233,0.06)] px-6 py-8 flex flex-col gap-6">
          {navLinks?.map((link) => (
            <a
              key={link?.label}
              href={link?.href}
              onClick={() => setMobileOpen(false)}
              className="text-[12px] font-semibold tracking-[0.2em] uppercase text-[#A09D97] hover:text-[#F2EFE9] transition-colors"
            >
              {link?.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={() => setMobileOpen(false)}
            className="btn-primary text-center text-[11px] font-bold tracking-[0.12em] mt-2"
          >
            <span>Schedule a Call</span>
          </a>
        </div>
      </div>
    </header>
  );
}